import { useRef, useState, type KeyboardEvent } from 'react';
import {
  ArrowRightIcon,
  CrownSimpleIcon,
  LinkSimpleIcon,
  PlusCircleIcon,
  SignInIcon,
  XIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { ModalGhostButton } from '@/features/teams/components/ModalGhostButton';
import { ModalPrimaryButton } from '@/features/teams/components/ModalPrimaryButton';
import { TeamModalShell } from '@/features/teams/components/TeamModalShell';
import { TEAM_ROLE_LABEL, tileColor } from '@/features/teams/components/teamVisuals';
import {
  acceptInvitation,
  createTeam,
  declineInvitation,
  inviteMember,
  useMyInvitations,
  type MyInvitation,
} from '@/api-models/teams';
import { teamPath, useSpace } from '@/contexts/space-context';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@/ui/Spinner';
import { Input } from '@/ui/Input';
import { Badge } from '@/ui/Badge';

const BRAND_DOMAIN = 'zooid.dev';

/** Mirror of the backend `slugify` so the preview matches the team's real slug. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** "Create or join a team" — a two-tab dialog: spin up a new team (with optional
 *  teammate invites) or accept invitations sent to your address. */
export function CreateTeamModal({ isOpen, onClose }: CreateTeamModalProps) {
  const { setActiveTeam, mutateTeams } = useSpace();
  const { invitations, isLoading: invitesLoading, mutate: mutateInvites } = useMyInvitations();
  const [tab, setTab] = useState<'create' | 'join'>('create');

  const [name, setName] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const draftRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const slug = slugify(name);

  function reset() {
    setTab('create');
    setName('');
    setEmails([]);
    setDraft('');
    setSubmitting(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function addEmail(raw: string) {
    const email = raw.trim().replace(/,$/, '').toLowerCase();
    if (!email) return;
    if (!isEmail(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!emails.includes(email)) setEmails((prev) => [...prev, email]);
    setDraft('');
  }

  function onDraftKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(draft);
    } else if (e.key === 'Backspace' && !draft && emails.length) {
      setEmails((prev) => prev.slice(0, -1));
    }
  }

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;

    // Fold in an un-committed draft email if it's valid.
    const pending = draft.trim().toLowerCase();
    const allEmails = pending && isEmail(pending) && !emails.includes(pending) ? [...emails, pending] : emails;

    setSubmitting(true);
    try {
      const { team } = await createTeam({ name: trimmed });
      // Email delivery is stubbed server-side; this just records the invitations.
      const results = await Promise.allSettled(allEmails.map((email) => inviteMember(team.id, { email })));
      const failed = results.filter((r) => r.status === 'rejected').length;

      mutateTeams();
      setActiveTeam(team);
      if (failed) toast.error(`Team created — ${failed} invite${failed > 1 ? 's' : ''} couldn't be sent`);
      else if (allEmails.length) toast.success(`Team created — ${allEmails.length} invited`);
      else toast.success('Team created');
      handleClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create team');
      setSubmitting(false);
    }
  }

  const footerActions =
    tab === 'create' ? (
      <>
        <ModalGhostButton onClick={handleClose}>Cancel</ModalGhostButton>
        <ModalPrimaryButton
          onClick={handleCreate}
          disabled={!name.trim()}
          isLoading={submitting}
          iconRight={<ArrowRightIcon size={15} />}
        >
          Create team
        </ModalPrimaryButton>
      </>
    ) : (
      <ModalGhostButton onClick={handleClose}>Done</ModalGhostButton>
    );

  const footerLeft =
    tab === 'create' ? (
      <span className="flex items-center gap-1.5 text-xs text-fg-tertiary">
        <CrownSimpleIcon size={14} />
        {"You'll be the owner"}
      </span>
    ) : invitations.length > 0 ? (
      <span className="text-xs text-fg-tertiary">
        {invitations.length} pending invitation{invitations.length > 1 ? 's' : ''}
      </span>
    ) : null;

  return (
    <TeamModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Create or join a team"
      subtitle="Teams are shared workspaces for you and your agents."
      footerLeft={footerLeft}
      footerActions={footerActions}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        nameRef.current?.focus();
      }}
    >
      {/* Tabs */}
      <div className="px-4 pt-3.5">
        <div className="flex gap-1 rounded-md border border-line-subtle bg-sunken p-1">
          <TabButton active={tab === 'create'} onClick={() => setTab('create')}>
            <PlusCircleIcon size={15} className={tab === 'create' ? 'text-fg' : 'text-fg-tertiary'} />
            Create new
          </TabButton>
          <TabButton active={tab === 'join'} onClick={() => setTab('join')}>
            <SignInIcon size={15} className={tab === 'join' ? 'text-fg' : 'text-fg-tertiary'} />
            Join a team
            {invitations.length > 0 && (
              <Badge variant="brand" size="sm" className="rounded-xs font-semibold">
                {invitations.length}
              </Badge>
            )}
          </TabButton>
        </div>
      </div>

      {tab === 'create' ? (
        <div className="flex flex-col gap-4 px-4 pb-4.5 pt-4">
          {/* Team name */}
          <div className="flex flex-col gap-1.75">
            <label htmlFor="team-name" className="text-xs font-medium text-fg-secondary">
              Team name
            </label>
            <Input
              ref={nameRef}
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Labs"
              className="h-11.5 rounded-md bg-surface-3 px-3.5 font-medium"
            />
            <div className="flex items-center gap-1 px-0.5">
              <LinkSimpleIcon size={13} className="text-fg-tertiary" />
              <span className="font-mono text-xs text-fg-tertiary">{BRAND_DOMAIN}/</span>
              <span className="font-mono text-xs font-medium text-info">{slug || 'your-team'}</span>
            </div>
          </div>

          {/* Invite teammates */}
          <div className="flex flex-col gap-1.75">
            <span className="text-xs font-medium text-fg-secondary">Invite teammates</span>
            <div
              onClick={() => draftRef.current?.focus()}
              className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-3 p-2.5"
            >
              {emails.map((email) => (
                <span
                  key={email}
                  className="flex h-7.5 items-center gap-2 rounded-sm border border-line-subtle bg-surface-4 pl-2.5 pr-1 text-xs font-medium text-fg"
                >
                  {email}
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEmails((prev) => prev.filter((x) => x !== email));
                    }}
                    className="flex size-5 items-center justify-center rounded-xs text-fg-tertiary transition-colors hover:bg-surface-3 hover:text-fg"
                  >
                    <XIcon size={12} />
                  </button>
                </span>
              ))}
              <input
                ref={draftRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onDraftKeyDown}
                onBlur={() => draft.trim() && addEmail(draft)}
                placeholder={emails.length ? '' : 'email@company.com'}
                className="h-7.5 min-w-35 flex-1 bg-transparent text-xs text-fg outline-none placeholder:text-fg-tertiary"
              />
            </div>
            <span className="text-xs text-fg-tertiary">{"Press Enter to add. They'll receive an email invite."}</span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 px-4 pb-4.5 pt-4">
          {invitesLoading ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size={5} />
            </div>
          ) : invitations.length === 0 ? (
            <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-line py-10 text-center">
              <span className="text-xs font-medium text-fg-secondary">No pending invitations</span>
              <span className="text-xs text-fg-tertiary">Invitations sent to your email will show up here.</span>
            </div>
          ) : (
            invitations.map((invite) => (
              <JoinRow
                key={invite.id}
                invite={invite}
                onResolved={() => {
                  mutateInvites();
                  mutateTeams();
                }}
                onClose={handleClose}
              />
            ))
          )}
        </div>
      )}
    </TeamModalShell>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-8.5 flex-1 items-center justify-center gap-1.75 rounded-sm text-xs transition-colors ${
        active
          ? 'border border-line-strong bg-surface-3 font-semibold text-fg'
          : 'border border-transparent font-medium text-fg-secondary hover:text-fg'
      }`}
    >
      {children}
    </button>
  );
}

function JoinRow({
  invite,
  onResolved,
  onClose,
}: {
  invite: MyInvitation;
  onResolved: () => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);

  async function accept() {
    setBusy('accept');
    try {
      await acceptInvitation(invite.id);
      onResolved();
      toast.success(`Joined ${invite.team_name}`);
      onClose();
      navigate(teamPath(invite.team_id, '/sessions'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation');
      setBusy(null);
    }
  }

  async function decline() {
    setBusy('decline');
    try {
      await declineInvitation(invite.id);
      onResolved();
      toast.success('Invitation declined');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to decline invitation');
      setBusy(null);
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-md border border-line-subtle bg-surface-3 p-3">
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-tile"
        style={{ backgroundColor: tileColor(invite.team_id) }}
      >
        <span className="font-mono text-xs font-semibold text-white">{invite.team_name.charAt(0).toUpperCase()}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13.5px] font-semibold text-fg">{invite.team_name}</span>
        <span className="truncate text-xs text-fg-tertiary">
          {TEAM_ROLE_LABEL[invite.role]} · invited by {invite.invited_by_name}
        </span>
      </div>
      <ModalGhostButton onClick={decline} disabled={busy !== null}>
        Decline
      </ModalGhostButton>
      <ModalPrimaryButton onClick={accept} isLoading={busy === 'accept'} disabled={busy !== null}>
        Accept
      </ModalPrimaryButton>
    </div>
  );
}
